// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet, Platform,
} from 'react-native';
import { Colors, FontSize, FontWeight, Radii } from '../../theme/tokens';
import { useApp } from '../../store/AppStore';
import { useResponsive } from '../../theme/responsive';
import { ScreenShell } from '../../components/WebShell';
import { ComingSoon } from '../../components/ComingSoon';
import { Icon } from '../../components/Icon';
import PrimaryButton from '../../components/Button_Component/Primary_Button';

// ─── Toggle ───────────────────────────────────────────────────────────────────

function Toggle({ on, onToggle, disabled }: { on: boolean; onToggle: () => void; disabled?: boolean }) {
  return (
    <TouchableOpacity
      disabled={disabled}
      onPress={onToggle}
      style={[
        styles.toggle,
        { backgroundColor: on ? Colors.accent : Colors.paper3, borderColor: on ? Colors.accent : Colors.lineStrong },
        disabled && styles.toggleDisabled,
      ]}
    >
      <View style={[styles.toggleThumb, { transform: [{ translateX: on ? 14 : 1 }] }]} />
    </TouchableOpacity>
  );
}

// ─── Email notification event types ─────────────────────────────────────────────
// Known set of server-emitted event types that can trigger email notifications.
// Persisted via orgSettings.notifierEventTypes.
const EMAIL_EVENTS = [
  { eventType: 'ticket.created',        label: 'New ticket created' },
  { eventType: 'comment.public_added',  label: 'Public reply added to a ticket' },
  { eventType: 'ticket.resolved',       label: 'Ticket resolved' },
  { eventType: 'ticket.closed',         label: 'Ticket closed' },
  { eventType: 'ticket.assigned',       label: 'Ticket assigned to an agent' },
  { eventType: 'sla.breached',          label: 'SLA breached' },
];

// ─── DND day picker ─────────────────────────────────────────────────────────────
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];

// Lightweight time stepper helpers (HH:MM, 30-min steps) — no native picker needed.
function clampTime(s: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s || '');
  if (!m) return '20:00';
  let h = Math.max(0, Math.min(23, parseInt(m[1], 10)));
  let mm = Math.max(0, Math.min(59, parseInt(m[2], 10)));
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}
function stepTime(s: string, deltaMin: number): string {
  const [h, mm] = clampTime(s).split(':').map(Number);
  let total = (h * 60 + mm + deltaMin + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

// ─── NotificationsScreen ──────────────────────────────────────────────────────

export function NotificationsScreen() {
  const { toast, orgSettings, saveOrgSettings, loadIntegrationsStatus } = useApp();
  const { isMobile, pagePadX } = useResponsive();

  // Honest integration config (null until loaded).
  const [integrationsStatus, setIntegrationsStatus] = useState<any | null>(null);

  // Map of eventType → email enabled.
  const [emailEnabled, setEmailEnabled] = useState<Record<string, boolean>>({});

  // DND schedule state.
  const [dndEnabled, setDndEnabled] = useState(false);
  const [dndStart, setDndStart] = useState('20:00');
  const [dndEnd, setDndEnd] = useState('08:00');
  const [dndDays, setDndDays] = useState<number[]>(ALL_DAYS);

  const [saving, setSaving] = useState(false);

  // Load real integration status on mount.
  useEffect(() => {
    loadIntegrationsStatus().then(s => setIntegrationsStatus(s)).catch(() => {});
  }, []);

  // Sync email + DND from orgSettings.
  useEffect(() => {
    if (!orgSettings) return;

    // Email event types — default to all on when unset.
    const map: Record<string, boolean> = {};
    if (Array.isArray(orgSettings.notifierEventTypes)) {
      EMAIL_EVENTS.forEach(e => { map[e.eventType] = orgSettings.notifierEventTypes.includes(e.eventType); });
    } else {
      EMAIL_EVENTS.forEach(e => { map[e.eventType] = true; });
    }
    setEmailEnabled(map);

    // DND schedule — null/absent means disabled.
    const dnd = orgSettings.dndSchedule;
    if (dnd && typeof dnd === 'object') {
      setDndEnabled(!!dnd.enabled);
      setDndStart(clampTime(dnd.start || '20:00'));
      setDndEnd(clampTime(dnd.end || '08:00'));
      setDndDays(Array.isArray(dnd.days) && dnd.days.length ? dnd.days : ALL_DAYS);
    } else {
      setDndEnabled(false);
      setDndStart('20:00');
      setDndEnd('08:00');
      setDndDays(ALL_DAYS);
    }
  }, [orgSettings]);

  const slackConfigured = !!integrationsStatus?.slack?.configured;
  const discordConfigured = !!integrationsStatus?.discord?.configured;

  const toggleEmail = (eventType: string) => {
    setEmailEnabled(prev => ({ ...prev, [eventType]: !prev[eventType] }));
  };

  const toggleDndDay = (day: number) => {
    setDndDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort((a, b) => a - b));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const notifierEventTypes = EMAIL_EVENTS.filter(e => emailEnabled[e.eventType]).map(e => e.eventType);
      const dndSchedule = dndEnabled
        ? { enabled: true, start: dndStart, end: dndEnd, days: dndDays }
        : null;
      await saveOrgSettings({ notifierEventTypes, dndSchedule });
    } finally {
      setSaving(false);
    }
  };

  // ── Showing the premium "coming soon" card for now. Original Notifications
  //    screen preserved below (unreachable) — remove this return to restore. ──
  return (
    <ComingSoon
      crumbs={[{ label: 'Settings' }, { label: 'Notifications' }]}
      icon="bell"
      title="Notifications"
      subtitle="Stay in the loop on exactly what matters — calm, smart alerts with zero noise. We're crafting it with care and it's almost here."
    />
  );

  return (
    <ScreenShell crumbs={[
      { label: 'Settings' },
      { label: 'Notifications' },
    ]}>
      <View style={styles.root}>
        <View style={[styles.pageHead, { paddingHorizontal: pagePadX }]}>
          <View style={{ flex: 1 }}>
            <Text style={styles.pageTitle}>Notifications</Text>
            <Text style={styles.pageSub}>Choose which events trigger email notifications, set quiet hours, and connect chat integrations.</Text>
          </View>
          <PrimaryButton
            title={saving ? 'Saving…' : 'Save changes'}
            size="md"
            variant="primary"
            disabled={saving}
            leftIcon={<Icon name="Check" size={14} color={Colors.paper} />}
            onPress={handleSave}
          />
        </View>

        <ScrollView style={styles.body} contentContainerStyle={[styles.bodyContent, { padding: isMobile ? 16 : 24 }]}>
          {/* Email notifications — real, persisted */}
          <Text style={styles.sectionTitle}>Email notifications</Text>
          <Text style={styles.sectionSub}>Pick the events that send an email. In-app notifications always show in the bell.</Text>

          <View style={[styles.card, { marginTop: 8 }]}>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={[styles.thText, { flex: 1 }]}>Event</Text>
              <Text style={[styles.thText, styles.thCenter, { width: 90 }]}>In-app</Text>
              <Text style={[styles.thText, styles.thCenter, { width: 90 }]}>Email</Text>
            </View>
            {EMAIL_EVENTS.map((r, i) => (
              <View key={r.eventType} style={[styles.tableRow, i < EMAIL_EVENTS.length - 1 && styles.rowBorder]}>
                <Text style={[styles.eventText, { flex: 1 }]}>{r.label}</Text>
                <View style={[styles.toggleCell, { width: 90 }]}>
                  {/* In-app delivery is always on (shown in the bell). */}
                  <Toggle on={true} disabled onToggle={() => {}} />
                </View>
                <View style={[styles.toggleCell, { width: 90 }]}>
                  <Toggle on={!!emailEnabled[r.eventType]} onToggle={() => toggleEmail(r.eventType)} />
                </View>
              </View>
            ))}
          </View>

          {/* DND — real, persisted */}
          <Text style={[styles.sectionTitle, { marginTop: 8 }]}>Do-not-disturb</Text>
          <Text style={styles.sectionSub}>Mute notifications during set times. Useful for nights and weekends.</Text>

          <View style={[styles.card, { marginTop: 8 }]}>
            <View style={styles.dndToggleRow}>
              <View style={{ flex: 1 }}>
                <Text style={styles.integTitle}>Enable quiet hours</Text>
                <Text style={styles.integSub}>
                  {dndEnabled ? 'Notifications are muted during the window below.' : 'Notifications are never muted.'}
                </Text>
              </View>
              <Toggle on={dndEnabled} onToggle={() => setDndEnabled(v => !v)} />
            </View>

            {dndEnabled && (
              <View style={styles.dndBody}>
                <View style={[styles.dndRow, isMobile && styles.gridStack]}>
                  <View style={styles.dndField}>
                    <Text style={styles.fieldLabel}>From</Text>
                    <View style={styles.timeStepper}>
                      <TouchableOpacity style={styles.stepBtn} onPress={() => setDndStart(t => stepTime(t, -30))}>
                        <Text style={styles.stepBtnText}>−</Text>
                      </TouchableOpacity>
                      <Text style={styles.timeText}>{dndStart}</Text>
                      <TouchableOpacity style={styles.stepBtn} onPress={() => setDndStart(t => stepTime(t, 30))}>
                        <Text style={styles.stepBtnText}>+</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                  <View style={styles.dndField}>
                    <Text style={styles.fieldLabel}>To</Text>
                    <View style={styles.timeStepper}>
                      <TouchableOpacity style={styles.stepBtn} onPress={() => setDndEnd(t => stepTime(t, -30))}>
                        <Text style={styles.stepBtnText}>−</Text>
                      </TouchableOpacity>
                      <Text style={styles.timeText}>{dndEnd}</Text>
                      <TouchableOpacity style={styles.stepBtn} onPress={() => setDndEnd(t => stepTime(t, 30))}>
                        <Text style={styles.stepBtnText}>+</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                </View>
                <View style={styles.dndField}>
                  <Text style={styles.fieldLabel}>Days</Text>
                  <View style={styles.daysRow}>
                    {DAY_LABELS.map((d, idx) => {
                      const active = dndDays.includes(idx);
                      return (
                        <TouchableOpacity
                          key={idx}
                          onPress={() => toggleDndDay(idx)}
                          style={[styles.dayChip, active && styles.dayChipActive]}
                        >
                          <Text style={[styles.dayChipText, active && styles.dayChipTextActive]}>{d}</Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </View>
              </View>
            )}
          </View>

          {/* Integrations — honest, config-driven */}
          <Text style={[styles.sectionTitle, { marginTop: 8 }]}>Integrations</Text>

          <View style={[styles.card, { marginTop: 8 }]}>
            {/* Slack */}
            <View style={[styles.integRow, styles.rowBorder]}>
              <View style={[styles.integIcon, { backgroundColor: '#4A154B' }]}>
                <Text style={styles.integIconText}>S</Text>
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.integTitle}>Slack</Text>
                <Text style={styles.integSub}>
                  Post notifications to a Slack channel.
                </Text>
              </View>
              {slackConfigured ? (
                <View style={[styles.statusPill, styles.statusPillOn]}>
                  <Text style={[styles.statusPillText, styles.statusPillTextOn]}>Configured</Text>
                </View>
              ) : (
                <View style={styles.statusPill}>
                  <Text style={styles.statusPillText}>Not configured</Text>
                </View>
              )}
            </View>
            {/* Discord */}
            <View style={styles.integRow}>
              <View style={[styles.integIcon, { backgroundColor: '#5865F2' }]}>
                <Text style={styles.integIconText}>D</Text>
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.integTitle}>Discord</Text>
                <Text style={styles.integSub}>Webhook-based delivery to a Discord channel.</Text>
              </View>
              {discordConfigured ? (
                <View style={[styles.statusPill, styles.statusPillOn]}>
                  <Text style={[styles.statusPillText, styles.statusPillTextOn]}>Configured</Text>
                </View>
              ) : (
                <View style={styles.statusPill}>
                  <Text style={styles.statusPillText}>Not configured</Text>
                </View>
              )}
            </View>
          </View>
        </ScrollView>
      </View>
    </ScreenShell>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: Colors.paper },
  pageHead: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap',
    paddingHorizontal: 28, paddingTop: 20, paddingBottom: 18,
    borderBottomWidth: 1, borderBottomColor: Colors.line,
    backgroundColor: Colors.card, flexShrink: 0,
  },
  pageTitle: { fontSize: 20, fontWeight: FontWeight.semibold, color: Colors.ink, letterSpacing: -0.2 },
  pageSub: { fontSize: 13, color: Colors.ink3, marginTop: 4 },
  toggle: {
    width: 32, height: 18, borderRadius: 999, borderWidth: 1,
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
  },
  toggleDisabled: { opacity: 0.55, ...(Platform.OS === 'web' ? { cursor: 'default' } as any : {}) },
  toggleThumb: {
    width: 14, height: 14, borderRadius: 99,
    backgroundColor: Colors.white,
    shadowColor: '#101828', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.18, shadowRadius: 2,
  },
  body: { flex: 1 },
  bodyContent: { maxWidth: 1000, alignSelf: 'center', width: '100%' as any, padding: 24 },
  sectionTitle: { fontSize: 14, fontWeight: FontWeight.semibold, color: Colors.ink },
  sectionSub: { fontSize: 12.5, color: Colors.ink3, marginTop: 2 },
  card: {
    backgroundColor: Colors.card, borderRadius: Radii.r3,
    borderWidth: 1, borderColor: Colors.line,
    marginBottom: 16, overflow: 'hidden',
  },
  tableRow: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 10 },
  tableHeader: { backgroundColor: Colors.paper2, borderBottomWidth: 1, borderBottomColor: Colors.line, paddingVertical: 8 },
  thText: { fontSize: 11.5, fontWeight: FontWeight.semibold, color: Colors.ink3 },
  thCenter: { textAlign: 'center' },
  rowBorder: { borderBottomWidth: 1, borderBottomColor: Colors.line },
  eventText: { fontSize: 13, color: Colors.ink },
  toggleCell: { alignItems: 'center', justifyContent: 'center' },
  dndToggleRow: { flexDirection: 'row', alignItems: 'center', gap: 16, padding: 16 },
  dndBody: { borderTopWidth: 1, borderTopColor: Colors.line, padding: 16, gap: 16 },
  dndRow: { flexDirection: 'row', gap: 20 },
  gridStack: { flexDirection: 'column', gap: 14 },
  dndField: { flex: 1, gap: 6 },
  fieldLabel: { fontSize: 13, fontWeight: FontWeight.medium, color: Colors.ink },
  timeStepper: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    borderWidth: 1, borderColor: Colors.line,
    borderRadius: Radii.r2, paddingHorizontal: 6, paddingVertical: 4,
    backgroundColor: Colors.card,
  },
  stepBtn: {
    width: 26, height: 26, borderRadius: Radii.r1,
    alignItems: 'center', justifyContent: 'center', backgroundColor: Colors.paper2,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
  },
  stepBtnText: { fontSize: 16, fontWeight: FontWeight.semibold, color: Colors.ink },
  timeText: { fontSize: 13.5, color: Colors.ink, fontWeight: FontWeight.medium },
  daysRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap' },
  dayChip: {
    paddingHorizontal: 10, paddingVertical: 6, borderRadius: Radii.r2,
    borderWidth: 1, borderColor: Colors.line, backgroundColor: Colors.card,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
  },
  dayChipActive: { backgroundColor: Colors.accent, borderColor: Colors.accent },
  dayChipText: { fontSize: 12.5, color: Colors.ink3, fontWeight: FontWeight.medium },
  dayChipTextActive: { color: Colors.paper },
  integRow: { flexDirection: 'row', alignItems: 'center', gap: 16, padding: 16 },
  integIcon: {
    width: 40, height: 40, borderRadius: Radii.r2,
    justifyContent: 'center', alignItems: 'center',
    flexShrink: 0,
  },
  integIconText: { fontSize: 16, fontWeight: FontWeight.bold, color: Colors.white },
  integTitle: { fontSize: 13.5, fontWeight: FontWeight.semibold, color: Colors.ink },
  integSub: { fontSize: 12, color: Colors.ink3, marginTop: 2 },
  statusPill: {
    paddingHorizontal: 10, paddingVertical: 5, borderRadius: 999,
    borderWidth: 1, borderColor: Colors.line, backgroundColor: Colors.paper2,
    flexShrink: 0,
  },
  statusPillOn: { borderColor: Colors.accent, backgroundColor: Colors.accentSoft || Colors.paper2 },
  statusPillText: { fontSize: 11.5, fontWeight: FontWeight.semibold, color: Colors.ink3 },
  statusPillTextOn: { color: Colors.accent },
});
